import * as styles from "./GlassdoorReviews.module.css";

import Slider, { Settings } from "react-slick";
import { SliderArrow, SliderDots } from "@components/general";
import {
  defaultSliderResponsiveObject,
  defaultSliderResponsiveSettings,
  defaultSliderSettings,
} from "@models";

import { HtmlOutput } from "@components/html";
import { IGlassdoorReviewsProps } from "@models/component-props";
import React from "react";
import { StaticImage } from "gatsby-plugin-image";

/**
 * Component which renders a carousel with Glassdoor reviews.
 *
 * @param {IGlassdoorReviewsProps} props
 * @param {IGlassdoorReviewsItem[]} props.glassdoorReviews
 *
 * @returns {JSX.Element}
 */
const GlassdoorReviews = ({ glassdoorReviews }: IGlassdoorReviewsProps) => {
  // Slider settings
  const sliderSettings: Settings = {
    ...defaultSliderSettings,
    slidesToShow: 3,
    slidesToScroll: 3,
    responsive: [
      {
        ...defaultSliderResponsiveObject,
        settings: {
          ...defaultSliderResponsiveSettings,
          // Dots with Glassdoor reviews styling
          appendDots: (dots) => {
            return (
              <SliderDots
                dots={dots}
                colour="green"
                dotsClassName={styles.glassdoorReviewsDots}
              />
            );
          },
          // Arrows with Glassdoor reviews styling
          nextArrow: (
            <SliderArrow
              direction="right"
              colour="green"
              arrowClassName={styles.glassdoorReviewsArrowRight}
            />
          ),
          prevArrow: (
            <SliderArrow
              direction="left"
              colour="green"
              arrowClassName={styles.glassdoorReviewsArrowLeft}
            />
          ),
        },
      },
    ],
  };

  return (
    <section className={`inner ${styles.glassdoorReviewsContainer}`}>
      {/* Title */}
      <h2>Glassdoor Reviews</h2>

      {/* Carousel */}
      <Slider {...sliderSettings}>
        {glassdoorReviews.map((gd, i) => {
          return (
            <div key={`gd_${i}`}>
              <div className={styles.glassdoorReviewsItem}>
                {/* Glassdoor stars - there can be 4/5 or 5/5 */}
                <div className={styles.glassdoorReviewsItemHeaderStars}>
                  {gd.stars === 5 ? (
                    <StaticImage
                      src="../../../../..//images/star-five-out-of-five-green.png"
                      alt="five green stars in a row"
                      width={138}
                    />
                  ) : (
                    <StaticImage
                      src="../../../../..//images/star-four-out-of-five-green.png"
                      alt="four green stars in a row and one blank star with a green border"
                      width={138}
                    />
                  )}
                </div>

                {/* Employee title, department, and how long they've been with the company */}
                <h3 className="h4">{gd.title}</h3>
                <h4 className="h5">{gd.department}</h4>
                <h5>Current Employee</h5>
                <p>{gd.current_employee}</p>

                {/* Employee's pros and cons for the company */}
                <h5>Pros:</h5>
                <HtmlOutput html={gd.pros} />
                <h5>Cons:</h5>
                <HtmlOutput html={gd.cons} />

                {/* Footer containing Glassdoor logo and link to more reviews */}
                <div className={styles.glassdoorReviewsItemFooter}>
                  <StaticImage
                    src="../../../../..//images/glassdoor-logo-white.png"
                    alt="Glassdoor logo in white"
                    width={120}
                  />
                  <a
                    href={
                      gd.link.trim().length > 0
                        ? gd.link
                        : "https://www.glassdoor.co.uk/Reviews/Rightio-Reviews-E1034485.htm"
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    Read our Glassdoor reviews
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </Slider>
    </section>
  );
};

export default GlassdoorReviews;
